import classNames from 'classnames';
import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import CloseButton from '@basic/CloseButton';
import { TRANSCRIPT_PENDING_MAX_AGE_MS } from '@consts';
import { EventType, NewMediaitemFileIsReadyData } from '@event-emitter';
import { useEventsListener } from '@hooks/useEventsListener';
import { useImprvApi } from '@hooks/useImprvApi';
import { MediaitemFileRole } from '@models/mediaitems';
import { TranscriptExpectation } from '@models/transcriptExpectations';
import * as Sentry from '@sentry/nextjs';
import { useBackgroundActivityStore } from '@services/backgroundActivityStore';
import { MediaitemService } from '@services/mediaitems';
import { setupForDark } from '@theme';
import { useInMemoryStorage } from 'lib/hooks/useInMemoryStorage';
import { useTranscriptExpectationsStore } from 'lib/stores/transcript_expectations_store';

const TRANSCRIPT_READY_TOAST_ID = 'transcript-ready';

const TranscriptReadyToastBody = ({
  expectation
}: {
  expectation: TranscriptExpectation | null;
}) => {
  const { t } = useTranslation();

  return (
    <div className='flex flex-col gap-xs' data-testid='transcript-ready-toast'>
      {/* RESPONSIVE STYLES */}
      <div className='P4R sm:P3R' data-testid='transcript-ready-toast-message'>
        {t('components.transcriptReadyToast.message')}
      </div>
      {expectation && (
        <>
          {/* RESPONSIVE STYLES */}
          <div
            className={classNames('P5R sm:P4R', setupForDark('text-tertiary'))}
            data-testid='transcript-ready-toast-filename'
          >
            {expectation.filename}
          </div>
          {/* RESPONSIVE STYLES */}
          <Link
            href={expectation.returnUrl}
            data-testid='transcript-ready-toast-open-task'
            onClick={() => toast.dismiss(TRANSCRIPT_READY_TOAST_ID)}
            className='P4M sm:P3M underline cursor-pointer'
          >
            {t('components.transcriptReadyToast.openTask')}
          </Link>
        </>
      )}
    </div>
  );
};

const TranscriptReadyToastCloseButton = ({ closeToast }: { closeToast: () => void }) => (
  <CloseButton
    onClick={closeToast}
    className='shrink-0 self-center aspect-square'
    data-testid='transcript-ready-toast-close'
  />
);

const TranscriptReadyToastListener = () => {
  const { on } = useEventsListener();
  const api = useImprvApi();
  const userId = useInMemoryStorage((state) => state.userId);
  const hydrateExpectations = useTranscriptExpectationsStore((state) => state.hydrateExpectations);
  const hydrateActivities = useBackgroundActivityStore((state) => state.hydrateActivities);
  const handledMediaitemIds = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!userId) return;
    hydrateExpectations();
    hydrateActivities();

    // A ready event fired while the app was closed never comes back.
    let cancelled = false;
    // Anything past the display cutoff shows no spinner either way, so it is not worth a request.
    const cutoff = Date.now() - TRANSCRIPT_PENDING_MAX_AGE_MS;
    const persistedIds = Object.entries(useTranscriptExpectationsStore.getState().expectations)
      .filter(([, expectation]) => expectation.createdAt >= cutoff)
      .map(([mediaitemId]) => Number(mediaitemId));

    Promise.all(
      persistedIds.map(async (mediaitemId) => {
        try {
          const mediaitem = await api.getMediaitem(mediaitemId);
          return new MediaitemService(mediaitem).isTranscriptSettled() ? mediaitemId : null;
        } catch (err) {
          if (err?.['status'] === 404) return mediaitemId;
          Sentry.captureException(err);
          return null;
        }
      })
    ).then((checked) => {
      if (cancelled) return;
      const settled = checked.filter((id): id is number => id !== null);
      if (settled.length) {
        useTranscriptExpectationsStore.getState().forgetExpectations(settled);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [userId, hydrateExpectations, hydrateActivities, api]);

  useEffect(() => {
    return on<NewMediaitemFileIsReadyData>(EventType.NEW_MEDIAITEM_FILE_IS_READY, (data) => {
      if (data.mediaitemFileRole !== MediaitemFileRole.TRANSCRIPT) return;
      if (handledMediaitemIds.current.has(data.mediaitemId)) return;
      handledMediaitemIds.current.add(data.mediaitemId);

      const expectation = useTranscriptExpectationsStore
        .getState()
        .takeExpectation(data.mediaitemId);
      const body = <TranscriptReadyToastBody expectation={expectation} />;

      if (toast.isActive(TRANSCRIPT_READY_TOAST_ID)) {
        toast.update(TRANSCRIPT_READY_TOAST_ID, { render: body });
        return;
      }

      toast.info(body, {
        toastId: TRANSCRIPT_READY_TOAST_ID,
        autoClose: false,
        closeButton: TranscriptReadyToastCloseButton,
        closeOnClick: false
      });
    });
  }, [on]);

  return null;
};

export default TranscriptReadyToastListener;
